import { useId, useMemo, useState } from 'react'
import { useHubLang } from '../lib/i18n'
import { SEARCH_PICK } from '../lib/hubCopy'
import { hasExact, matchOptions, type PickOption } from '../lib/searchPick'

/**
 * 321 (FED-74). Type to find, pick from what matches: the one picker for a
 * photo's equipment, sector, feature, brand and site (David, 29 Oct: "all of
 * them should be searchable"). It replaces the <select>s, which listed every
 * value at once and could not be searched.
 *
 * An ARIA combobox: the list opens on focus and narrows as you type; arrows
 * move, Enter picks, Escape closes the list (and only the list — the dialog
 * around it keeps its own Escape for the next press). With `onCreate`, text
 * that matches nothing exactly is offered as "Add “…”" at the foot of the list.
 * The list is never longer than 50; typing is how you reach the rest.
 */
export function SearchPick<T extends PickOption>({
  options,
  label,
  placeholder,
  onPick,
  onCreate,
  disabled = false,
  autoFocus = false,
  className,
}: {
  options: T[]
  /** The accessible name, and the placeholder unless one is given. */
  label: string
  placeholder?: string
  onPick: (o: T) => void
  /** Offer what was typed as a new value. Leave out where nothing new may be added. */
  onCreate?: (text: string) => void
  disabled?: boolean
  autoFocus?: boolean
  className?: string
}) {
  const t = SEARCH_PICK[useHubLang()]
  const id = useId()
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const shown = useMemo(() => matchOptions(options, q, 50), [options, q])
  const canCreate = !!onCreate && q.trim().length >= 2 && !hasExact(options, q)
  const total = shown.length + (canCreate ? 1 : 0)

  function choose(i: number) {
    if (i < shown.length) onPick(shown[i])
    else if (canCreate && onCreate) onCreate(q.trim())
    else return
    setQ('')
    setOpen(false)
    setActive(0)
  }

  return (
    <div className={className ? `searchpick ${className}` : 'searchpick'}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        aria-activedescendant={open && total > 0 ? `${id}-o${Math.min(active, total - 1)}` : undefined}
        aria-label={label}
        placeholder={placeholder ?? label}
        value={q}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="off"
        maxLength={120}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
          setActive(0)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setOpen(true)
            setActive((a) => Math.min(Math.max(total - 1, 0), a + 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((a) => Math.max(0, a - 1))
          } else if (e.key === 'Enter') {
            if (open && total > 0) {
              e.preventDefault()
              choose(Math.min(active, total - 1))
            }
          } else if (e.key === 'Escape' && open) {
            // The list owns this press; the dialog around it keeps the next one.
            e.stopPropagation()
            setOpen(false)
          }
        }}
      />
      {open && (
        <ul id={`${id}-list`} role="listbox" aria-label={label} className="searchpick__list">
          {shown.map((o, i) => (
            <li
              key={o.id}
              id={`${id}-o${i}`}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'searchpick__opt is-active' : 'searchpick__opt'}
              // Keep focus in the input, so the click lands before the blur closes the list.
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(i)}
            >
              <span className="searchpick__label">{o.label}</span>
              {o.hint ? <span className="searchpick__hint">{o.hint}</span> : null}
            </li>
          ))}
          {canCreate && (
            <li
              id={`${id}-o${shown.length}`}
              role="option"
              aria-selected={active === shown.length}
              className={active === shown.length ? 'searchpick__opt searchpick__new is-active' : 'searchpick__opt searchpick__new'}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(shown.length)}
              onClick={() => choose(shown.length)}
            >
              {t.addNew(q.trim())}
            </li>
          )}
          {total === 0 && (
            <li className="searchpick__none" role="presentation">
              {t.noMatch}
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
